import { manufacturingFetch } from "@/lib/manufacturingClient";
import type { LaborPayInvoiceLine } from "@/lib/manufacturing/laborPayInvoiceLine";
import type { LaborPayReviewSide, LaborPayStatus } from "@/lib/manufacturing/laborPayList";
import type { LaborPayCutoffConfig } from "@/lib/manufacturing/laborPayWeek";

export type LaborPayListRow = {
  unit_id: string;
  entry_id: string | null;
  serial: string;
  recipe_id: string | null;
  recipe_label: string;
  phase_id: string | null;
  phase_name: string;
  plant_id: string | null;
  payout_usd: number;
  rate_item_count: number;
  unmatched_labor_count: number;
  unmatched_model_count: number;
  status: LaborPayStatus;
  ops_reviewed_at: string | null;
  plant_reviewed_at: string | null;
  payment_requested_at: string | null;
  pay_batch_id: string | null;
  in_pay_trigger_column: boolean;
  itemized: boolean;
  admitted_at?: string | null;
  option_labels?: string;
};

export type LaborPayWeekPreview = {
  cutoff_at: string;
  week_ending: string;
  config: LaborPayCutoffConfig;
};

export function listManufacturingLaborPayList(plantId?: string) {
  const q = new URLSearchParams();
  if (plantId) q.set("plant_id", plantId);
  const qs = q.toString();
  const path = qs ? `/api/mfg/labor-pay-list?${qs}` : "/api/mfg/labor-pay-list";
  return manufacturingFetch<{
    ok: true;
    rows: LaborPayListRow[];
    missing_table: boolean;
  }>(path, { method: "GET" });
}

export function reviewManufacturingLaborPayList(payload: {
  entry_id?: string | null;
  unit_id: string;
  side: LaborPayReviewSide;
}) {
  return manufacturingFetch<{ ok: true; entry: { id: string; status: LaborPayStatus } }>(
    "/api/mfg/labor-pay-list/review",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

export function requestManufacturingLaborPay(entryId: string) {
  return manufacturingFetch<{ ok: true; entry: { id: string; status: LaborPayStatus } }>(
    "/api/mfg/labor-pay-list/request",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entry_id: entryId }),
    }
  );
}

export function previewManufacturingLaborPayWeek() {
  return manufacturingFetch<{ ok: true; preview: LaborPayWeekPreview }>(
    "/api/mfg/labor-pay-list/batch",
    { method: "GET" }
  );
}

export function saveManufacturingLaborPayCutoff(config: LaborPayCutoffConfig) {
  return manufacturingFetch<{ ok: true; preview: LaborPayWeekPreview }>(
    "/api/mfg/labor-pay-list/batch",
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(config),
    }
  );
}

export function closeManufacturingLaborPayWeek() {
  return manufacturingFetch<{
    ok: true;
    batch: { id: string; cutoff_at: string; week_ending: string; entry_count: number };
  }>("/api/mfg/labor-pay-list/batch", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
}

export type LaborPayMatchLine = {
  line: { key: string; option_key: string; product_key: string; label: string; quantity: number; price: number | null };
  labor: "matched" | "unmatched" | "included" | "needs_pays_by" | "needs_size";
  labor_rate_id: string | null;
  amount_usd: number;
  count: number;
  model: "matched" | "unmatched";
  model_label: string | null;
};

export type LaborPayMatchUnit = {
  unit_id: string;
  serial: string;
  recipe_id: string | null;
  recipe_label: string;
  phase_name: string;
  payout_usd: number;
  unmatched_labor_count: number;
  unmatched_model_count: number;
  lines: LaborPayMatchLine[];
};

export type LaborPayInvoiceDetail = {
  unit_id: string;
  entry_id: string | null;
  serial: string;
  payout_usd: number;
  status: LaborPayStatus;
  frozen: boolean;
  lines_computed_at: string | null;
  unmatched_labor_count: number;
  unmatched_model_count: number;
  lines: LaborPayInvoiceLine[];
};

export function getManufacturingLaborPayInvoice(unitId: string) {
  const q = new URLSearchParams({ unit_id: unitId });
  return manufacturingFetch<{ ok: true; invoice: LaborPayInvoiceDetail }>(
    `/api/mfg/labor-pay-list/invoice?${q.toString()}`,
    { method: "GET" }
  );
}

export function recalculateManufacturingLaborPayInvoice(unitId: string) {
  return manufacturingFetch<{ ok: true; invoice: LaborPayInvoiceDetail }>(
    "/api/mfg/labor-pay-list/invoice",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ unit_id: unitId, recalculate: true }),
    }
  );
}

export function listManufacturingLaborPayMatches(plantId?: string) {
  const q = new URLSearchParams();
  if (plantId) q.set("plant_id", plantId);
  const qs = q.toString();
  const path = qs ? `/api/mfg/labor-pay-list/matches?${qs}` : "/api/mfg/labor-pay-list/matches";
  return manufacturingFetch<{ ok: true; units: LaborPayMatchUnit[] }>(path, { method: "GET" });
}
